/**
 * @component IonInputComponent
 *
 * This component serves as a reusable wrapper around Ionic's input elements,
 * allowing for easy integration within reactive forms. It dynamically applies form control bindings,
 * label placement, custom styling, and optionally wraps the input within an `ion-item` for better
 * UI consistency.
 *
 * @inputs
 * - `templateFormGroup` (FormGroup): The parent form group containing the form control.
 * - `formField` (ReceivingFormInput): Object representing field configuration (name, type, validation, etc.).
 * - `formControlName` (string): The name of the form control within the form group.
 * - `labelPlacement` (string, optional): Defines where the label should be placed (e.g., "floating").
 * - `className` (string, optional): Additional CSS classes to be applied for styling.
 * - `wrapWithItem` (boolean, default: false): If true, wraps the input inside an `ion-item` for better styling.
 * - `disabledCondition` (boolean, default: false): If true, disables the input based on external conditions.
 * - `datalistOptions` (string[], optional): Array of strings to provide autocomplete suggestions via HTML5 datalist.
 *
 * @lifecycle_hooks
 * - `ngOnChanges()`: Detects changes in the `formField` input and dynamically enables/disables
 *   the corresponding form control.
 */

import {
  Component,
  OnChanges,
  SimpleChanges,
  Input,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ReceivingFormInput } from '../../../models/assets-data/receiving/ReceivingLogFormConfig.interface';

@Component({
  selector: 'app-ion-input',
  templateUrl: './ion-input.component.html',
  styleUrls: ['./ion-input.component.css'],
})
export class IonInputComponent implements OnChanges {
  @ViewChild('customInput', { read: ElementRef }) inputElementRef!: ElementRef;

  /** The parent form group containing the form controls */
  @Input() templateFormGroup!: FormGroup;

  /** The field configuration defining the input's behavior */
  @Input() formField!: ReceivingFormInput;

  /** The name of the form control, used for binding it to the form */
  @Input() formControlName!: string;

  /** Determines the label placement (e.g., 'floating', 'stacked', 'fixed') */
  @Input() labelPlacement?: string;

  /** Custom CSS classes for styling the input */
  @Input() className?: string;

  /** If true, wraps the input within an `ion-item` for consistent UI */
  @Input() wrapWithItem: boolean = false;

  /** If true, disables the input based on an external condition */
  @Input() disabledCondition: boolean = false;

  constructor() {}

  /**
   * Lifecycle hook: Called when any of the input properties change.
   *
   * This method listens for changes in the `formField` input and updates the form control's
   * disabled state accordingly. If the `formField.disabled` property is true, the form control
   * will be disabled; otherwise, it will be enabled.
   *
   * @param changes - An object representing the changed input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('formField' in changes) {
      const formField = changes['formField'].currentValue;

      if (this.templateFormGroup && formField) {
        const control = this.templateFormGroup.get(formField.name);

        if (control) {
          if (formField.disabled) {
            control.disable(); // Disable the form control
          } else {
            control.enable(); // Enable the form control
          }
        }
      }
    }
  }

  /**
   * Get error text for the form control
   */
  public getErrorText(): string {
    const control = this.templateFormGroup?.get(this.formControlName);
    
    if (!control || !control.touched) {
      return '';
    }

    // // Log all errors for debugging
    // if (control.errors) {
    //   console.log(`Errors for ${this.formControlName}:`, control.errors);
    // }

    // Check for specific errors
    if (control.hasError('required')) {
      return `${this.formField.label} is required`;
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('occupied')) {
      return 'This space is already occupied by this job';
    }

    // Fallback to custom error message if provided
    if (this.formField.errorMsg) {
      return this.formField.errorMsg;
    }

    return '';
  }
}
